type RuntimeEnvironment = "local" | "dev" | "preview" | "preprod-gcp3" | "live" | "prod-gcp3";

export interface RuntimeConfig {
    environment: RuntimeEnvironment;
    apiBaseUrl: string;
    uiBaseUrl: string;
    redirectUri: string;
    postLogoutRedirectUri: string;
}

type RuntimeConfigPayload = Partial<{
    environment: string;
    apiBaseUrl: string;
    uiBaseUrl: string;
    redirectUri: string;
    postLogoutRedirectUri: string;
}>;

let runtimeConfigCache: RuntimeConfig | null = null;

const PREPROD_FALLBACK_CONFIG: RuntimeConfig = {
    environment: "preprod-gcp3",
    apiBaseUrl: "https://api.vehicle.ford.com/vsu-release",
    uiBaseUrl: "https://srp-ui.preprod.vsu.ford.com/",
    redirectUri: "https://srp-ui.preprod.vsu.ford.com/",
    postLogoutRedirectUri: "https://srp-ui.preprod.vsu.ford.com/",
};

function isRuntimeEnvironment(value: string): value is RuntimeEnvironment {
    return value === "local" || value === "dev" || value === "dev-gcp3" || value === "preview" || value === "preprod-gcp3" || value === "live" || value === "prod-gcp3";
}

function normalizeConfigPayload(payload: RuntimeConfigPayload): RuntimeConfig | null {
    const environment = typeof payload.environment === "string" ? payload.environment.toLowerCase() : "";
    const apiBaseUrl = payload.apiBaseUrl;
    const uiBaseUrl = payload.uiBaseUrl;
    const redirectUri = payload.redirectUri;
    const postLogoutRedirectUri = payload.postLogoutRedirectUri;

    if (!isRuntimeEnvironment(environment)) {
        return null;
    }

    if (!apiBaseUrl || !uiBaseUrl || !redirectUri || !postLogoutRedirectUri) {
        return null;
    }

    return {
        environment,
        apiBaseUrl,
        uiBaseUrl,
        redirectUri,
        postLogoutRedirectUri,
    };
}

function isLocalhost(hostname: string): boolean {
    return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
}

function applyLocalRedirectOverride(config: RuntimeConfig): RuntimeConfig {
    if (typeof window === "undefined") {
        return config;
    }

    if (!isLocalhost(window.location.hostname)) {
        return config;
    }

    const localOrigin = window.location.origin;

    return {
        ...config,
        redirectUri: localOrigin,
        postLogoutRedirectUri: localOrigin,
    };
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
    if (runtimeConfigCache) {
        return runtimeConfigCache;
    }

    if (typeof window === "undefined") {
        throw new Error("Runtime config must be loaded in browser context.");
    }

    let normalizedConfig: RuntimeConfig | null = null;

    try { 
        // BEDROCK_ENVIRONMENT 
        const response = await fetch("/config", { cache: "no-store" });
        if (response.ok) {
            const payload = (await response.json()) as RuntimeConfigPayload;
            normalizedConfig = normalizeConfigPayload(payload);
        }
    } catch (error) {
        // Ignore fetch/network errors and apply preview fallback below.
        console.error("Error loading runtime config, applying fallback if in preview environment:", error);
    }

    runtimeConfigCache = applyLocalRedirectOverride(normalizedConfig ?? PREPROD_FALLBACK_CONFIG);
    return runtimeConfigCache;
}

export function getRuntimeConfig(): RuntimeConfig {
    if (runtimeConfigCache) {
        return runtimeConfigCache;
    }

    throw new Error("Runtime config not loaded. Call loadRuntimeConfig() first.");
}
